import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Zap,
  CreditCard,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  ShieldCheck,
  Info,
} from "lucide-react";

const BUNDLE_CHOICES = [1000, 2000, 5000, 10000];

const STATUS_TONE = {
  succeeded: { text: "text-emerald-500 dark:text-emerald-400", label: "SUCCEEDED" },
  failed: { text: "text-rose-500 dark:text-rose-400", label: "FAILED" },
  requires_action: { text: "text-amber-500 dark:text-amber-400", label: "3DS REQUIRED" },
  skipped_cap: { text: "text-muted-foreground", label: "SKIPPED · CAP" },
  skipped_recent: { text: "text-muted-foreground", label: "SKIPPED · 24H" },
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }) : "—";

/**
 * Coerce any axios error into a human-readable string. FastAPI validation
 * errors (422) return `detail` as an array of objects — passing that
 * straight into toast/JSX crashes React with "Objects are not valid as a
 * React child". This helper flattens every shape into a single string.
 */
function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d[0]?.msg || d[0]?.message || fallback || "Request failed";
  if (d && typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return err?.message || fallback || "Request failed";
}

/**
 * Auto Top-Up settings + saved card + history — mounted inside Billing.
 * Talks to /api/cdn-topup/* endpoints. Card save uses Stripe Checkout in
 * setup mode; on return the frontend detects ?setup=success&session_id
 * and calls /finalize-card automatically.
 */
export default function AutoTopUpPanel({ canWrite }) {
  const [cfg, setCfg] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null); // "save-card" | "remove" | null
  const [retired, setRetired] = useState(null);  // null = unknown until /r2/status resolves

  useEffect(() => {
    api.get("/r2/status").then((r) => setRetired(!!r.data?.enabled)).catch(() => setRetired(false));
  }, []);

  const load = useCallback(async () => {
    try {
      const [c, e] = await Promise.all([
        api.get("/cdn-topup/config"),
        api.get("/cdn-topup/events?limit=20"),
      ]);
      setCfg(c.data);
      setEvents(e.data?.events || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[auto-topup] load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe Checkout redirect-back
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setup = params.get("setup");
    const sessionId = params.get("session_id");
    if (!setup) return;
    // Clean the URL so a refresh doesn't re-trigger the finalize
    const clean = window.location.pathname;
    window.history.replaceState({}, "", clean);
    if (setup === "success" && sessionId) {
      (async () => {
        try {
          await api.post(`/cdn-topup/finalize-card?session_id=${encodeURIComponent(sessionId)}`);
          toast.success("Payment method saved");
          load();
        } catch (err) {
          toast.error(errMsg(err, "Failed to save card"));
        }
      })();
    } else if (setup === "cancel") {
      toast.info("Card setup cancelled");
    }
  }, [load]);

  const patchConfig = async (patch) => {
    if (!canWrite) return;
    setSaving(true);
    // Optimistic update
    const prev = cfg;
    setCfg((c) => ({ ...c, ...normalizeForOptimistic(patch, c) }));
    try {
      const r = await api.patch("/cdn-topup/config", patch);
      // Merge instead of replace so any field the PATCH response happens
      // to omit (e.g. bundle_price_usd on older builds) doesn't blank out.
      setCfg((c) => ({ ...c, ...r.data }));
    } catch (err) {
      setCfg(prev);
      toast.error(errMsg(err, "Failed to update settings"));
    } finally {
      setSaving(false);
    }
  };

  const saveCard = async () => {
    if (!canWrite) return;
    setBusy("save-card");
    try {
      const r = await api.post("/cdn-topup/save-card", {
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Could not start card setup");
      }
    } catch (err) {
      toast.error(errMsg(err, "Card setup failed"));
      setBusy(null);
    }
  };

  const removeCard = async () => {
    if (!canWrite) return;
    if (!window.confirm("Remove saved card? Auto top-up will be disabled.")) return;
    setBusy("remove");
    try {
      const r = await api.delete("/cdn-topup/payment-method");
      setCfg(r.data);
      toast.success("Card removed");
    } catch (err) {
      toast.error(errMsg(err, "Failed to remove card"));
    } finally {
      setBusy(null);
    }
  };

  if (retired !== false) return null;
  if (loading) {
    return (
      <div className="rounded-md border border-border bg-card/40 backdrop-blur-md p-6 flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!cfg) return null;

  const capUsedPct = cfg.monthly_cap_usd > 0
    ? Math.min(100, Math.round((cfg.month_spent_usd / cfg.monthly_cap_usd) * 100))
    : 0;

  return (
    <div className="rounded-md border border-border bg-card/40 backdrop-blur-md p-5 space-y-5" data-testid="auto-topup-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-fuchsia-500/20 to-cyan-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-fuchsia-500" />
          </div>
          <div>
            <h3 className="font-display text-lg" data-testid="auto-topup-title">
              CDN Auto Top-Up
            </h3>
            <p className="text-xs text-muted-foreground max-w-xl">
              Automatically charges your card when Cloudflare Stream minutes get low, so uploads never fail at 4 AM. Guardrails: max 1 charge per 24h &amp; a monthly cap you set.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none" title={canWrite ? undefined : "Read-only for your role"}>
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            {cfg.enabled ? "ENABLED" : "DISABLED"}
          </span>
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            disabled={!canWrite || !cfg.has_payment_method}
            onChange={(e) => patchConfig({ cdn_topup_enabled: e.target.checked })}
            data-testid="auto-topup-enabled-toggle"
            className="w-4 h-4 accent-fuchsia-500"
          />
        </label>
      </div>

      {/* Warning when enabled without card */}
      {cfg.enabled && !cfg.has_payment_method && (
        <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-sm bg-amber-500/12 border border-amber-500/40 text-amber-500 dark:text-amber-300">
          <AlertTriangle className="w-3.5 h-3.5" />
          Enabled but no saved card — nothing will fire until you add one below.
        </div>
      )}

      {/* Settings grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Threshold */}
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Fire at
          </label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="range"
              min={60}
              max={95}
              step={1}
              value={cfg.threshold_pct}
              disabled={!canWrite || saving}
              onChange={(e) => patchConfig({ cdn_topup_threshold_pct: Number(e.target.value) })}
              data-testid="auto-topup-threshold"
              className="flex-1 accent-fuchsia-500"
            />
            <span className="font-mono text-sm tabular-nums w-12 text-right">
              {cfg.threshold_pct}%
            </span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            of Cloudflare minutes used
          </div>
        </div>

        {/* Bundle */}
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Bundle size
          </label>
          <select
            value={cfg.bundle_minutes}
            disabled={!canWrite || saving}
            onChange={(e) => patchConfig({ cdn_topup_bundle_minutes: Number(e.target.value) })}
            data-testid="auto-topup-bundle"
            className="mt-1 w-full text-sm bg-background border border-border rounded-sm px-2 py-1.5"
          >
            {BUNDLE_CHOICES.map((m) => (
              <option key={m} value={m}>{m.toLocaleString()} min</option>
            ))}
          </select>
          <div className="text-[10px] text-muted-foreground mt-1">
            <b className="text-foreground">${cfg.bundle_price_usd?.toFixed(2)}</b> per top-up · <span className="capitalize">{cfg.plan}</span> plan rate ${cfg.per_1000min_usd?.toFixed(2)}/1000
          </div>
        </div>

        {/* Cap — local state so intermediate values (<10) don't 422 on every
            keystroke. Committed on blur with clamping. */}
        <CapInput
          value={cfg.monthly_cap_usd}
          disabled={!canWrite || saving}
          onCommit={(v) => patchConfig({ cdn_topup_monthly_cap_usd: v })}
          capUsedPct={capUsedPct}
          monthSpent={cfg.month_spent_usd}
          monthKey={cfg.month_key}
        />
      </div>

      {/* Payment method */}
      <div className="border-t border-border pt-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-muted-foreground" />
          {cfg.has_payment_method ? (
            <div>
              <div className="text-sm capitalize" data-testid="auto-topup-card-summary">
                {cfg.card_brand || "Card"} •••• {cfg.card_last4 || "----"}
              </div>
              <div className="text-[10px] text-muted-foreground">
                Exp {String(cfg.card_exp_month || "??").padStart(2, "0")}/{String(cfg.card_exp_year || "??").slice(-2)} · stored securely by Stripe
              </div>
            </div>
          ) : (
            <div>
              <div className="text-sm">No card on file</div>
              <div className="text-[10px] text-muted-foreground">
                Add one to activate auto top-up
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {cfg.has_payment_method ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={saveCard}
                disabled={!canWrite || busy === "save-card"}
                data-testid="auto-topup-replace-card"
                className="text-[10px] font-mono uppercase tracking-widest"
              >
                {busy === "save-card" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Replace"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={removeCard}
                disabled={!canWrite || busy === "remove"}
                data-testid="auto-topup-remove-card"
                className="text-[10px] font-mono uppercase tracking-widest text-rose-500 hover:text-rose-500"
              >
                {busy === "remove" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </Button>
            </>
          ) : (
            canWrite && (
              <Button
                onClick={saveCard}
                disabled={busy === "save-card"}
                data-testid="auto-topup-save-card"
                className="text-[10px] font-mono uppercase tracking-widest"
              >
                {busy === "save-card" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <ShieldCheck className="w-3.5 h-3.5 mr-2" />}
                Add card
              </Button>
            )
          )}
        </div>
      </div>

      {/* Last error surface */}
      {cfg.last_error && (
        <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-sm bg-rose-500/10 border border-rose-500/30 text-rose-500 dark:text-rose-400" data-testid="auto-topup-last-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <div className="font-mono uppercase tracking-widest text-[10px]">LAST ATTEMPT FAILED</div>
            <div className="text-foreground/80">{cfg.last_error}</div>
          </div>
        </div>
      )}

      {/* History */}
      {events.length > 0 && (
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Recent top-ups
            </span>
          </div>
          <div className="space-y-1 text-xs" data-testid="auto-topup-history">
            {events.slice(0, 10).map((e) => {
              const tone = STATUS_TONE[e.status] || { text: "text-muted-foreground", label: e.status?.toUpperCase() };
              return (
                <div key={e.id} className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-sm hover:bg-accent/50" data-testid={`auto-topup-event-${e.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {e.status === "succeeded" ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    ) : (
                      <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${tone.text}`} />
                    )}
                    <span className="text-muted-foreground tabular-nums w-32 shrink-0">{fmtDate(e.at)}</span>
                    <span className="font-mono tabular-nums w-16">${e.amount_usd?.toFixed(2)}</span>
                    <span className="text-muted-foreground truncate">
                      {e.bundle_minutes.toLocaleString()} min · at {e.percent_used_at_trigger}% quota
                      {e.error ? ` · ${e.error}` : ""}
                    </span>
                  </div>
                  <span className={`font-mono text-[10px] tracking-widest shrink-0 ${tone.text}`}>
                    {tone.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Optimistic-update helper — for `enabled` we mirror it directly; for
// threshold/bundle/cap the API echoes back the updated snapshot so we don't
// need to recompute derived fields here.
function normalizeForOptimistic(patch, prev) {
  const out = {};
  if ("cdn_topup_enabled" in patch) out.enabled = patch.cdn_topup_enabled;
  if ("cdn_topup_threshold_pct" in patch) out.threshold_pct = patch.cdn_topup_threshold_pct;
  if ("cdn_topup_bundle_minutes" in patch) out.bundle_minutes = patch.cdn_topup_bundle_minutes;
  if ("cdn_topup_monthly_cap_usd" in patch) out.monthly_cap_usd = patch.cdn_topup_monthly_cap_usd;
  return out;
}

/**
 * Monthly-cap input with LOCAL state so an intermediate value (e.g. "3"
 * while the user is typing "300") never fires a 422 PATCH. The parent's
 * setting is only updated on blur / Enter with a clamped value.
 */
function CapInput({ value, disabled, onCommit, capUsedPct, monthSpent, monthKey }) {
  const [local, setLocal] = useState(String(value ?? ""));
  useEffect(() => { setLocal(String(value ?? "")); }, [value]);

  const commit = () => {
    const n = Number(local);
    if (!Number.isFinite(n)) { setLocal(String(value ?? "")); return; }
    const clamped = Math.max(10, Math.min(5000, Math.round(n)));
    if (clamped !== value) onCommit(clamped);
    setLocal(String(clamped));
  };

  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        Monthly cap
      </label>
      <div className="mt-1 flex items-center gap-1">
        <span className="text-muted-foreground text-sm">$</span>
        <input
          type="number"
          min={10}
          max={5000}
          step={5}
          value={local}
          disabled={disabled}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
          data-testid="auto-topup-cap"
          className="w-full text-sm bg-background border border-border rounded-sm px-2 py-1.5 tabular-nums"
        />
      </div>
      <div className="mt-2 h-1 bg-accent rounded-full overflow-hidden" data-testid="auto-topup-cap-track">
        <div
          className={`h-1 rounded-full transition-all ${capUsedPct >= 90 ? "bg-rose-500" : capUsedPct >= 60 ? "bg-amber-400" : "bg-emerald-400"}`}
          style={{ width: `${capUsedPct}%` }}
          data-testid="auto-topup-cap-bar"
        />
      </div>
      <div className="text-[10px] text-muted-foreground mt-1" data-testid="auto-topup-cap-detail">
        ${Number(monthSpent || 0).toFixed(2)} spent this month ({monthKey})
      </div>
    </div>
  );
}
